import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col, Card, Button, Spinner, Alert, InputGroup } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import AssignedManagersReadOnly from '../components/AssignedManagersReadOnly';
import { PartyBadge, PartyBadgeList } from '../components/PartyBadge';
import AIEnrichmentButton from '../components/AIEnrichmentButton';
import CandidateRaceAssignments from '../components/CandidateRaceAssignments';
import { adminApi, extractError } from '../api/client';
import { STATUS_OPTIONS } from '../lib/lookups';
import { useStates, useCounties } from '../hooks/useLocations';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const EMPTY = {
  full_name: '',
  slug: '',
  photo_url: '',
  biography: '',
  campaign_website: '',
  email: '',
  phone: '',
  social_media: { twitter: '', facebook: '', instagram: '', linkedin: '', youtube: '', website: '' },
  key_issues: [],
  endorsements: [],
  source_links: [],
  status: 'draft',
  internal_notes: '',
  // Admin-only editorial trust signal. Only mutable via the admin form;
  // campaign managers see it read-only. Backend enforces this via
  // MANAGER_BLOCKED_FIELDS.
  verified: false,
  home_state_id: null,
  home_county_id: null,
  // Canonical party at the candidate level. Optional — a candidate profile
  // can legitimately exist with no party set (independent, refactor). The
  // per-race binding may override this via the Race Assignments section.
  party: '',
};

function Section({ step, title, children }) {
  return (
    <div className="form-section">
      <div className="form-section-header">
        <span className="step">{step}</span>
        <h3>{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function CandidateFormPage({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const { user: me } = useAuth();
  const isAdmin = me?.role === 'admin';
  const isManager = me?.role === 'campaign_manager';
  const [data, setData] = useState(EMPTY);

  // Extracted as a useCallback-style closure so the AI Enrichment modal
  // can re-trigger a fresh load after a successful apply/undo without
  // duplicating the fetch logic. Returns true on success.
  const reloadCandidate = async () => {
    if (mode !== 'edit') return false;
    try {
      const res = await adminApi.get(`/admin/candidates/${id}`);
      const d = res.data || {};
      d.social_media = { ...EMPTY.social_media, ...(d.social_media || {}) };
      d.key_issues = d.key_issues || [];
      d.endorsements = d.endorsements || [];
      d.source_links = d.source_links || [];
      setData(d);
      return true;
    } catch (e) {
      setError(extractError(e));
      return false;
    }
  };
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  // Locations
  const { states } = useStates();
  const linkedStateId = data.home_state_id || null;
  const { counties, loading: countiesLoading } = useCounties(linkedStateId);

  useEffect(() => {
    if (mode !== 'edit') return;
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/candidates/${id}`);
        if (!active) return;
        const d = res.data || {};
        // Ensure nested defaults
        d.social_media = { ...EMPTY.social_media, ...(d.social_media || {}) };
        d.key_issues = d.key_issues || [];
        d.endorsements = d.endorsements || [];
        d.source_links = d.source_links || [];
        setData(d);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, mode]);

  function update(field, value) {
    setData((d) => {
      const next = { ...d, [field]: value };
      // When home state changes, clear home_county_id (it must belong to the state)
      if (field === 'home_state_id') {
        next.home_county_id = null;
      }
      return next;
    });
  }
  function updateNested(field, key, value) {
    setData((d) => ({ ...d, [field]: { ...(d[field] || {}), [key]: value } }));
  }
  function updateListItem(field, index, key, value) {
    setData((d) => {
      const list = [...(d[field] || [])];
      list[index] = { ...list[index], [key]: value };
      return { ...d, [field]: list };
    });
  }
  function addListItem(field, item) {
    setData((d) => ({ ...d, [field]: [...(d[field] || []), item] }));
  }
  function removeListItem(field, index) {
    setData((d) => ({ ...d, [field]: (d[field] || []).filter((_, i) => i !== index) }));
  }

  function validate() {
    const errs = {};
    if (!data.full_name?.trim()) errs.full_name = 'Required';
    if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errs.email = 'Invalid email';
    if (data.campaign_website && !/^https?:\/\//i.test(data.campaign_website)) errs.campaign_website = 'Must start with http(s)://';
    if (data.photo_url && !/^https?:\/\//i.test(data.photo_url)) errs.photo_url = 'Must start with http(s)://';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e, overrideStatus) {
    e.preventDefault();
    setError('');
    if (!validate()) {
      setError('Please correct the highlighted fields.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...data };
      if (overrideStatus) payload.status = overrideStatus;
      // Campaign managers cannot mutate these fields; strip them client-side
      // so we don't trip the backend's 403 protection on benign saves.
      if (isManager) {
        delete payload.status;
        delete payload.internal_notes;
        delete payload.assigned_user_ids;
        delete payload.verified;
      }
      if (mode === 'create') {
        const res = await adminApi.post('/admin/candidates', payload);
        toast.success('Candidate created');
        navigate(`/candidates/${res.data.id}`);
      } else {
        const res = await adminApi.put(`/admin/candidates/${id}`, payload);
        toast.success('Candidate updated');
        navigate(`/candidates/${res.data.id}`);
      }
    } catch (e) {
      const msg = extractError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title={mode === 'create' ? 'New candidate' : 'Edit candidate'} />
        <div className="text-center p-5"><Spinner animation="border" /></div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={mode === 'create' ? 'New candidate' : `Edit — ${data.full_name}`}
        breadcrumb={
          <span><Link to={isAdmin ? '/candidates' : '/my-candidates'} className="text-decoration-none">{isAdmin ? 'Candidates' : 'My Candidates'}</Link> / {mode === 'create' ? 'New' : 'Edit'}</span>
        }
        actions={
          <div className="d-flex gap-2 flex-wrap">
            {/* AI Enrichment is admin-only (matches backend require_admin gate).
                Hidden in create-mode because the candidate has no id yet — the
                LLM also needs race participations to ground its research, so
                applying after the first save is the better workflow. */}
            {mode === 'edit' && isAdmin && id && (
              <AIEnrichmentButton
                candidateId={id}
                hasHistory={(data.ai_enrichment_history?.length || 0) > 0}
                onApplied={reloadCandidate}
                onUndone={reloadCandidate}
              />
            )}
            <Button variant="outline-secondary" onClick={() => navigate(isAdmin ? '/candidates' : '/my-candidates')}>Cancel</Button>
            {isAdmin && (
              <Button variant="outline-primary" onClick={(e) => handleSubmit(e, 'draft')} disabled={saving} data-testid="candidate-form-save-draft-button">
                Save draft
              </Button>
            )}
            <Button variant="primary" onClick={(e) => handleSubmit(e)} disabled={saving} data-testid="candidate-form-save-button">
              {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        {/* Party + race participation summary — read-only because party
            is stored per race_candidate (one candidate can technically
            register under different parties across cycles). Editing the
            party of an individual race participation happens on the Race
            page, not here on the Candidate. */}
        {/* Race Assignments — optional, editable. A candidate profile
            may have zero assigned races (per refactor). Party is stored
            canonically on the candidate itself in the Basic Info section
            below; each race binding may carry its own per-race party
            override for edge cases (party switch). */}
        {mode === 'edit' && (
          <CandidateRaceAssignments
            candidateId={id}
            participations={data.race_participations || []}
            defaultParty={data.party || ''}
            onChange={reloadCandidate}
          />
        )}
        <Form noValidate onSubmit={(e) => handleSubmit(e)}>
          <Row className="g-3">
            <Col xs={12} lg={8}>
              <Card>
                <Card.Body>
                  <Section step={1} title="Basic Info">
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Full name <span className="text-danger">*</span></Form.Label>
                          <Form.Control isInvalid={!!errors.full_name} value={data.full_name} onChange={(e) => update('full_name', e.target.value)} data-testid="candidate-full-name-input" />
                          <Form.Control.Feedback type="invalid">{errors.full_name}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Slug</Form.Label>
                          <Form.Control value={data.slug || ''} onChange={(e) => update('slug', e.target.value)} placeholder="auto-generated from name" data-testid="candidate-slug-input" />
                          <Form.Text>Lowercase, hyphenated. Auto-generated if blank.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Photo URL</Form.Label>
                          <Form.Control isInvalid={!!errors.photo_url} value={data.photo_url || ''} onChange={(e) => update('photo_url', e.target.value)} placeholder="https://…" data-testid="candidate-photo-url-input" />
                          <Form.Control.Feedback type="invalid">{errors.photo_url}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Home state <span className="text-muted">(optional)</span></Form.Label>
                          <Form.Select value={data.home_state_id || ''} onChange={(e) => update('home_state_id', e.target.value || null)} data-testid="candidate-home-state-select">
                            <option value="">— None —</option>
                            {(states || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </Form.Select>
                          <Form.Text>Where the candidate is FROM. Race scope (which office, district) is set on the Race, not here.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Home county <span className="text-muted">(optional)</span></Form.Label>
                          <Form.Select
                            value={data.home_county_id || ''}
                            onChange={(e) => update('home_county_id', e.target.value || null)}
                            disabled={!linkedStateId || countiesLoading}
                            data-testid="candidate-home-county-select"
                          >
                            <option value="">
                              {!linkedStateId
                                ? 'Pick a state first'
                                : countiesLoading
                                ? 'Loading counties…'
                                : counties.length === 0
                                ? 'No counties for this state'
                                : '— None —'}
                            </option>
                            {counties.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}{c.county_code ? ` (${c.county_code})` : ''}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Party <span className="text-muted">(optional)</span></Form.Label>
                          <InputGroup>
                            <Form.Select
                              value={data.party || ''}
                              onChange={(e) => update('party', e.target.value)}
                              data-testid="candidate-party-select"
                            >
                              <option value="">— No party affiliation —</option>
                              <option value="Democratic">Democratic (DEM)</option>
                              <option value="Republican">Republican (REP)</option>
                              <option value="Libertarian">Libertarian (LIB)</option>
                              <option value="Green">Green (GRE)</option>
                              <option value="Independent">Independent (IND)</option>
                              <option value="No Party Affiliation">No Party Affiliation (NPA)</option>
                              <option value="Write-in">Write-in (WRI)</option>
                              {/* Preserve any legacy free-text value the DB already holds */}
                              {data.party && !['Democratic','Republican','Libertarian','Green','Independent','No Party Affiliation','Write-in'].includes(data.party) && (
                                <option value={data.party}>{data.party} (custom)</option>
                              )}
                            </Form.Select>
                            {data.party && (
                              <Button
                                variant="outline-secondary"
                                onClick={() => update('party', '')}
                                title="Clear party"
                                data-testid="candidate-party-clear"
                              >
                                <i className="bi bi-x" />
                              </Button>
                            )}
                          </InputGroup>
                          <Form.Text>Candidate&apos;s canonical party. Per-race overrides live on each Race Assignment above.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>Biography</Form.Label>
                          <Form.Control as="textarea" rows={5} value={data.biography || ''} onChange={(e) => update('biography', e.target.value)} data-testid="candidate-biography-input" />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Section>

                  <Section step={2} title="Campaign Info">
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Campaign website</Form.Label>
                          <Form.Control isInvalid={!!errors.campaign_website} value={data.campaign_website || ''} onChange={(e) => update('campaign_website', e.target.value)} placeholder="https://…" data-testid="candidate-website-input" />
                          <Form.Control.Feedback type="invalid">{errors.campaign_website}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Email</Form.Label>
                          <Form.Control isInvalid={!!errors.email} value={data.email || ''} onChange={(e) => update('email', e.target.value)} data-testid="candidate-email-input" />
                          <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Phone</Form.Label>
                          <Form.Control value={data.phone || ''} onChange={(e) => update('phone', e.target.value)} data-testid="candidate-phone-input" />
                        </Form.Group>
                      </Col>
                      {[
                        ['twitter', 'Twitter / X', 'bi-twitter-x'],
                        ['facebook', 'Facebook', 'bi-facebook'],
                        ['instagram', 'Instagram', 'bi-instagram'],
                        ['linkedin', 'LinkedIn', 'bi-linkedin'],
                        ['youtube', 'YouTube', 'bi-youtube'],
                      ].map(([key, label, icon]) => (
                        <Col xs={12} md={6} key={key}>
                          <Form.Group>
                            <Form.Label>{label}</Form.Label>
                            <InputGroup>
                              <InputGroup.Text><i className={`bi ${icon}`} /></InputGroup.Text>
                              <Form.Control
                                value={data.social_media?.[key] || ''}
                                onChange={(e) => updateNested('social_media', key, e.target.value)}
                                placeholder="@handle or https://…"
                                data-testid={`candidate-social-${key}-input`}
                              />
                            </InputGroup>
                            <Form.Text className="text-muted">
                              Accepts either a handle (<code>@repbyrondonalds</code>) or a full URL.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      ))}
                    </Row>
                  </Section>

                  <Section step={3} title="Issues / Positions">
                    {(data.key_issues || []).map((issue, i) => (
                      <Row className="g-2 align-items-start mb-2" key={i}>
                        <Col xs={12} md={4}>
                          <Form.Control placeholder="Title" value={issue.title || ''} onChange={(e) => updateListItem('key_issues', i, 'title', e.target.value)} data-testid={`issue-title-${i}`} />
                        </Col>
                        <Col xs={12} md={7}>
                          <Form.Control placeholder="Description" value={issue.description || ''} onChange={(e) => updateListItem('key_issues', i, 'description', e.target.value)} data-testid={`issue-desc-${i}`} />
                        </Col>
                        <Col xs={12} md={1}>
                          <Button variant="outline-danger" onClick={() => removeListItem('key_issues', i)} aria-label="Remove issue" data-testid={`issue-remove-${i}`}>
                            <i className="bi bi-trash" />
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button variant="outline-secondary" size="sm" onClick={() => addListItem('key_issues', { title: '', description: '' })} data-testid="add-issue-button">
                      <i className="bi bi-plus-lg me-1" /> Add issue
                    </Button>

                    <hr className="my-4" />
                    <h4 style={{ fontSize: '0.95rem', fontWeight: 600 }}>Endorsements</h4>
                    {(data.endorsements || []).map((en, i) => (
                      <Row className="g-2 align-items-start mb-2" key={i}>
                        <Col xs={12} md={4}>
                          <Form.Control placeholder="Organization" value={en.organization || ''} onChange={(e) => updateListItem('endorsements', i, 'organization', e.target.value)} data-testid={`endorsement-org-${i}`} />
                        </Col>
                        <Col xs={12} md={4}>
                          <Form.Control placeholder="Description" value={en.description || ''} onChange={(e) => updateListItem('endorsements', i, 'description', e.target.value)} />
                        </Col>
                        <Col xs={12} md={3}>
                          <Form.Control placeholder="URL" value={en.url || ''} onChange={(e) => updateListItem('endorsements', i, 'url', e.target.value)} />
                        </Col>
                        <Col xs={12} md={1}>
                          <Button variant="outline-danger" onClick={() => removeListItem('endorsements', i)} aria-label="Remove endorsement">
                            <i className="bi bi-trash" />
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button variant="outline-secondary" size="sm" onClick={() => addListItem('endorsements', { organization: '', description: '', url: '' })} data-testid="add-endorsement-button">
                      <i className="bi bi-plus-lg me-1" /> Add endorsement
                    </Button>
                  </Section>

                  <Section step={4} title="Sources">
                    {(data.source_links || []).map((s, i) => (
                      <Row className="g-2 align-items-start mb-2" key={i}>
                        <Col xs={12} md={4}>
                          <Form.Control placeholder="Title" value={s.title || ''} onChange={(e) => updateListItem('source_links', i, 'title', e.target.value)} data-testid={`source-title-${i}`} />
                        </Col>
                        <Col xs={12} md={7}>
                          <Form.Control placeholder="https://…" value={s.url || ''} onChange={(e) => updateListItem('source_links', i, 'url', e.target.value)} data-testid={`source-url-${i}`} />
                        </Col>
                        <Col xs={12} md={1}>
                          <Button variant="outline-danger" onClick={() => removeListItem('source_links', i)} aria-label="Remove source">
                            <i className="bi bi-trash" />
                          </Button>
                        </Col>
                      </Row>
                    ))}
                    <Button variant="outline-secondary" size="sm" onClick={() => addListItem('source_links', { title: '', url: '' })} data-testid="add-source-button">
                      <i className="bi bi-plus-lg me-1" /> Add source
                    </Button>
                  </Section>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              {isAdmin ? (
                <div className="d-flex flex-column gap-3" style={{ position: 'sticky', top: 12 }}>
                  <Card>
                    <Card.Header>Status & notes</Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select value={data.status || 'draft'} onChange={(e) => update('status', e.target.value)} data-testid="candidate-form-status-select">
                          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </Form.Select>
                        <Form.Text>Workflow: Draft → In Review → Reviewed → Published → Archived</Form.Text>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <Form.Label className="mb-0 d-flex align-items-center gap-2">
                              {data.verified && <i className="bi bi-patch-check-fill text-success" aria-hidden />}
                              Verified profile
                            </Form.Label>
                            <Form.Text className="d-block">Confirm core facts (name, party, office, links) match official sources.</Form.Text>
                          </div>
                          <Form.Check
                            type="switch"
                            id="candidate-verified-switch"
                            checked={!!data.verified}
                            onChange={(e) => update('verified', e.target.checked)}
                            data-testid="candidate-form-verified-switch"
                            aria-label="Mark candidate profile as verified"
                          />
                        </div>
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Internal notes</Form.Label>
                        <Form.Control as="textarea" rows={5} value={data.internal_notes || ''} onChange={(e) => update('internal_notes', e.target.value)} placeholder="Editor-only notes (never exposed via public API)" data-testid="candidate-internal-notes" />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                  <AssignedManagersReadOnly assignedIds={data.assigned_user_ids || []} />
                </div>
              ) : (
                <Card style={{ position: 'sticky', top: 12 }} data-testid="manager-readonly-status-card">
                  <Card.Header>Status</Card.Header>
                  <Card.Body>
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Current status: <strong>{data.status || 'draft'}</strong>
                    </div>
                    {data.verified && (
                      <div className="mt-2 text-success d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }} data-testid="manager-verified-indicator">
                        <i className="bi bi-patch-check-fill" aria-hidden />
                        <span>Verified profile</span>
                      </div>
                    )}
                    <div className="text-muted mt-2" style={{ fontSize: '0.8rem' }}>
                      Only administrators can change a candidate&apos;s status, verification, or internal notes.
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
}
